// src/components/ui/MediaSlider.jsx

import React, { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Thumbs } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import '@/styles/vendor-overrides/swiper.css';
import MediaPreview from '@/components/ui/MediaPreview';

/**
 * MediaSlider
 *
 * Props:
 *   files               — array of { file_url, file_type }
 *   onActiveFileChange  — optional callback(file) fired when active slide changes
 */
const MediaSlider = ({ files, onActiveFileChange }) => {
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const videoRefs = useRef({});

  if (!files || files.length === 0) return null;

  const handleSlideChange = (swiper) => {
    Object.entries(videoRefs.current).forEach(([idx, video]) => {
      if (Number(idx) !== swiper.activeIndex && video) {
        video.pause();
      }
    });

    if (onActiveFileChange) {
      onActiveFileChange(files[swiper.activeIndex]);
    }
  };

  return (
    <div className="w-full space-y-4">
      <Swiper
        modules={[Navigation, Pagination, Thumbs]}
        navigation
        thumbs={{ swiper: thumbsSwiper }}
        noSwiping={true}
        noSwipingClass="swiper-no-swiping"
        touchStartPreventDefault={false}
        onSlideChange={handleSlideChange}
        className="w-full h-[65vh]"
      >
        {files.map((file, idx) => (
          <SwiperSlide key={idx}>
            <div className="w-full h-full flex justify-center items-center bg-black">
              <MediaPreview
                ref={(el) => {
                  if (el) videoRefs.current[idx] = el;
                }}
                file={{
                  file_url: file.file_url,
                  file_type: file.file_type,
                }}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {files.length > 1 && (
        <div className="w-full flex justify-center">
          <Swiper
            modules={[Thumbs]}
            onSwiper={setThumbsSwiper}
            watchSlidesProgress
            slidesPerView="auto"
            spaceBetween={8}
            slideToClickedSlide
            className="w-full h-[100px] mt-2"
            wrapperClass="!flex !justify-center"
          >
            {files.map((file, idx) => (
              <SwiperSlide
                key={`thumb-${idx}`}
                className="
                  !w-[100px] !h-[100px]
                  flex items-center justify-center
                  flex-shrink-0 cursor-pointer
                  rounded-lg overflow-hidden
                  opacity-60 hover:opacity-100
                  transition-all duration-200 ease-in-out
                  swiper-slide-thumb-active:opacity-100
                  swiper-slide-thumb-active:scale-105
                "
              >
                <MediaPreview
                  thumbnail
                  file={{
                    file_url: file.file_url,
                    file_type: file.file_type,
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
    </div>
  );
};

export default MediaSlider;